import { targetWatts, type Step, type Target, type Workout } from './model';

export interface Segment {
  index: number;
  /** Start time within the workout (s). */
  start: number;
  durationSec: number;
  kind: 'steady' | 'ramp' | 'free';
  from?: Target;
  to?: Target;
  cadence?: number;
  label?: string;
  /** 1-based repetition info when the segment came from a repeat block. */
  rep?: { n: number; of: number };
}

/** Flatten steps (with nested repeats) into a timeline (SPEC §7.2). */
export function flatten(steps: Step[]): Segment[] {
  const out: Segment[] = [];
  let t = 0;
  const walk = (list: Step[], rep?: { n: number; of: number }) => {
    for (const s of list) {
      if (s.kind === 'repeat') {
        for (let i = 0; i < s.times; i++) walk(s.steps, { n: i + 1, of: s.times });
        continue;
      }
      if (s.durationSec <= 0) continue;
      const seg: Segment = { index: out.length, start: t, durationSec: s.durationSec, kind: s.kind };
      if (s.label) seg.label = s.label;
      if (rep) seg.rep = rep;
      if (s.kind === 'steady') {
        seg.from = s.target;
        seg.to = s.target;
        if (s.cadence) seg.cadence = s.cadence;
      } else if (s.kind === 'ramp') {
        seg.from = s.from;
        seg.to = s.to;
      }
      out.push(seg);
      t += s.durationSec;
    }
  };
  walk(steps);
  return out;
}

export function totalDuration(steps: Step[] | Segment[]): number {
  const segs = isSegments(steps) ? steps : flatten(steps);
  const last = segs[segs.length - 1];
  return last ? last.start + last.durationSec : 0;
}

function isSegments(x: Step[] | Segment[]): x is Segment[] {
  return x.length > 0 && 'start' in (x[0] as object);
}

/** Target watts at time `t` inside a segment (ramps interpolate). null for free segments. */
export function segmentWatts(seg: Segment, t: number, ftp: number): number | null {
  if (seg.kind === 'free' || !seg.from) return null;
  const a = targetWatts(seg.from, ftp);
  if (seg.kind === 'steady' || !seg.to) return a;
  const b = targetWatts(seg.to, ftp);
  const f = seg.durationSec > 0 ? Math.max(0, Math.min(1, (t - seg.start) / seg.durationSec)) : 0;
  return a + (b - a) * f;
}

export function findSegment(segs: Segment[], t: number): number {
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i]!;
    if (t < s.start + s.durationSec) return i;
  }
  return segs.length;
}

/** Per-second power profile (free segments assumed at `freeFraction` × FTP) — for charts and TSS estimates. */
export function powerProfile(w: Workout | Step[], ftp: number, freeFraction = 0.6): number[] {
  const segs = flatten(Array.isArray(w) ? w : w.steps);
  const out: number[] = [];
  for (const s of segs) {
    for (let i = 0; i < s.durationSec; i++) {
      out.push(segmentWatts(s, s.start + i + 0.5, ftp) ?? freeFraction * ftp);
    }
  }
  return out;
}
